import {
  isCompletedRegistrationStatus,
  isRejectedEntityReviewStatus,
  readProgressCustomerNumber,
} from './registration-progress-compat';
import type { RegistrationProgressResponse } from './status-display';

export const REGISTRATION_SUCCESS_PLACEHOLDERS = {
  customerNumber: '—',
  accountStatusPending: 'Menunggu',
} as const;

export interface RegistrationSuccessInfo {
  customerNumber: string;
  registeredOutletCount: number;
  accountStatusLabel: string;
  usesCustomerNumberPlaceholder: boolean;
}

export function extractCustomerNumber(
  progress: RegistrationProgressResponse
): string | null {
  return readProgressCustomerNumber(progress);
}

export function isRegistrationCompletedStatus(
  progress: RegistrationProgressResponse
): boolean {
  return isCompletedRegistrationStatus(progress);
}

export function areAllSavedRegistrationSectionsComplete(
  progress: RegistrationProgressResponse
): boolean {
  const { sections } = progress;

  if (!sections.business.saved) {
    return false;
  }

  const business = sections.business;
  if (
    !business.complete ||
    business.missing_fields.length > 0 ||
    business.missing_documents.length > 0 ||
    isRejectedEntityReviewStatus(business.status)
  ) {
    return false;
  }

  if (sections.outlets.per_outlet.length === 0) {
    return false;
  }

  return sections.outlets.per_outlet.every(
    (outlet) =>
      outlet.complete &&
      outlet.missing_fields.length === 0 &&
      outlet.missing_attachments.length === 0 &&
      !isRejectedEntityReviewStatus(outlet.status)
  );
}

export function shouldShowRegistrationSuccessBanner(
  progress: RegistrationProgressResponse
): boolean {
  if (progress.registration_status === 'pending_review') {
    return false;
  }

  return (
    isRegistrationCompletedStatus(progress) ||
    extractCustomerNumber(progress) !== null
  );
}

export function extractRegistrationSuccessInfo(
  progress: RegistrationProgressResponse
): RegistrationSuccessInfo {
  const registeredOutletCount =
    progress.sections.outlets.count || progress.outlets.length;
  const rawCustomerNumber = extractCustomerNumber(progress);
  const hasActiveAccount =
    isRegistrationCompletedStatus(progress) || rawCustomerNumber !== null;

  return {
    customerNumber:
      rawCustomerNumber ?? REGISTRATION_SUCCESS_PLACEHOLDERS.customerNumber,
    registeredOutletCount,
    accountStatusLabel: hasActiveAccount
      ? 'Aktif'
      : REGISTRATION_SUCCESS_PLACEHOLDERS.accountStatusPending,
    usesCustomerNumberPlaceholder: rawCustomerNumber === null,
  };
}
